import { useState, useEffect } from "react";

import { useApolloClient, useMutation } from "@apollo/client";

import { FilterMatchMode, FilterOperator } from "primereact/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { BOOKINGS } from "../../../provider/Bookings/query";
import { useUser } from "../../../context/users";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { CANCEL_BOOKING, COMPLETED_BOOKING } from "../../../provider/Bookings/mutation";
import { useSettings } from "../../../context/settings";
import { getGraphsqErros } from "../../../utilities/functions";
import REASONS from "../../../provider/Reasons/query";

const Races = () => {
  const settings = useSettings();
  const apollo = useApolloClient();
  const [cancelBooking, { loading: canceling }] = useMutation(CANCEL_BOOKING);
  const [completeBooking, { loading: completing }] = useMutation(COMPLETED_BOOKING);

  const user = useUser();
  const franchise_id = user?.getUser()?.franchise?.id;
  const acting_region_id = user?.getUser()?.actingRegion?.id;
  const is_callcenter = user?.getUser()?.is_callcenter;

  const [franchise, setFranchise] = useState<any>(null);
  const [franchises, setFranchises] = useState<iFranchise[]>([]);
  const [region, setRegion] = useState<any>(null);
  const [regions, setRegions] = useState<iRegion[]>([]);
  const [load, setLoad] = useState(true);
  const [reload, setReload] = useState<boolean>(false);

  const [bookings, setBookings] = useState<any>(null);
  const [showBooking, setShowBooking] = useState<any>();
  const [reasons, setReasons] = useState<iReason[]>([]);
  const [filters, setFilters] = useState<any>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [notifieds, setNotifieds] = useState<any>(null);
  const [notifiedsDialog, setNotifiedsDialog] = useState<boolean>(false);
  const [bookingID, setBookingID] = useState<number>();

  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelBookingId, setCancelBookingId] = useState<any>();
  const [cancelReason, setCancelReason] = useState<any>();

  const [completeDialog, setCompleteDialog] = useState(false);
  const [completeBookingId, setCompleteBookingId] = useState<any>()

  const [reloadBookings, setReloadBookings] = useState(0);

  const states = [
    "AWAIT",
    "ACCEPTED_DRIVER",
    "CANCELED_DRIVER",
    "CANCELED_PASSENGER",
    "ON_ROUTE",
    "EMBARKATION",
    "EXPIRED",
    "COMPLETED",
  ];

  const [driverNames, setDriverNames] = useState([]);

  useEffect(() => {
    getBookings();

    initFilters1();
  }, [region?.code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (franchise?.code){
      setRegion(null);
      setReload(true);
      getBookings();
  
      initFilters1();
    }
  }, [franchise?.code]);

  useEffect(() => {
    loadReasons();
  }, [])

  useEffect(() => {
    if (reloadBookings > 0) getBookings();
  }, [reloadBookings]); // eslint-disable-line react-hooks/exhaustive-deps

  const getBookings = () => {
    setLoading(true);
    const filter: any = {limit: 100};

    if (franchise_id) filter.franchise_id = Number(franchise_id);
    if (acting_region_id) filter.acting_region_id = Number(acting_region_id);

    if (franchise?.code) filter.franchise_id = Number(franchise.code);

    if (region?.code) filter.acting_region_id = Number(region.code);

    if (is_callcenter) filter.passenger_id = user.getUser()?.passenger_id;

    apollo
      .query({
        query: BOOKINGS,
        variables: filter,
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.bookings;
        setBookings(mapBookings(data));

        setFranchises(response.data.franchises);
        setRegions(response.data.regions);
        setLoading(false);
        setLoad(false);
        setReload(false);
      })
      .catch(error => {
        console.log('getBookings error: ', JSON.stringify(error));
        setLoading(false);
        setLoad(false);
        setReload(false);
      });
  };

  function loadReasons() {
    apollo
      .query({
        query: REASONS,
        variables: { type: "PASSENGER", page: "CANCELLATION" },
        fetchPolicy: "no-cache",
      })
      .then(response => {
        setReasons(response.data.reasons);
      })
      .catch(e => {});
  }

  const cancel = (booking_id: number, reason_id: number) => {
    cancelBooking({
      variables: {
        booking_id: parseInt(`${booking_id}`),
        reason_id: parseInt(`${reason_id}`),
        state: "CANCELED_PASSENGER",
      },
    })
      .then(e => {
        setReloadBookings(reloadBookings + 1)
        setCancelDialog(false);
        setCancelBookingId(null);
        setCancelReason(null);
      })
      .catch(e => {
        console.log('cancelBooking error: ', JSON.stringify(e));
        settings?.setErrorMessage(getGraphsqErros(e));
        settings?.setVisibleError(true);
      });
  }

  const complete = (booking_id: number) => {
    completeBooking({
      variables: {
        booking_id: parseInt(`${booking_id}`),
      },
    })
      .then(e => {
        setReloadBookings(reloadBookings + 1)
        setCompleteDialog(false);
        setCompleteBookingId(null);
      })
      .catch(e => {
        console.log('completeBooking error: ', JSON.stringify(e));
        settings?.setErrorMessage(getGraphsqErros(e));
        settings?.setVisibleError(true);
      });
  }

  const mapBookings = (data: any) => {
    return [...data].map((d: any) => {
      return {
        id: d.id,
        driver: d.driver?.name,
        passenger: d.passenger?.name,
        phone: d.passenger?.phone,
        destiny_address: d.destiny_address,
        origin_address: d.origin_address,
        created_at: new Date(d.created_at),
        distance: d.distance,
        price: d.offered_value_driver || d.original_value,
        state: d.state,
        stops: d.stops,
        service: d.service?.name,
        is_callcenter: d.is_callcenter || false,
        notifiedDrivers: d.notifiedDrivers,
        notifieds: d.notifieds,
        canceled_at: d.canceled_at ? new Date(d.canceled_at) : undefined,
        accepted_at: d.accepted_at ? new Date(d.accepted_at) : undefined,
        completed_at: d.completed_at ? new Date(d.completed_at) : undefined,
        route_at: d.route_at ? new Date(d.route_at) : undefined,
      }
    });
  };

  const formatDate = (date: any) => {
    return `${date.getDate()}/${date.getMonth()+1}/${date.getFullYear()} ${date.getHours()}:${date.getMinutes() > 9 ? '' : '0'}${date.getMinutes()}`;
  };

  const initFilters1 = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      driver: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      passenger: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      phone: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
      created_at: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
      destiny_address: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      origin_address: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      state: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    });
  };

  const hideNotifiedsDialog = () => {
    setNotifiedsDialog(false);
    setNotifieds(undefined);
    setBookingID(undefined);
  };

  const openNotifieds = (record: any) => {
    const notifiedDrivers = record.notifiedDrivers.map((item: any, i) => {
      return {
        id: item.id,
        name: item.name,
        phone: item.phone,
        version: item.version,
        received: record.notifieds[i].received,
      }
    })

    setNotifieds(notifiedDrivers);
    setNotifiedsDialog(true);
    setBookingID(record.id);
  };

  const idBodyTemplate = (rowData: any) => {
    return (
      <div style={{display: 'flex', alignItems: 'center'}}>
        {`${(rowData.id)} `}
        <Button
          icon="pi pi-search"
          className="p-button-rounded p-button-warning p-button-text mr-2 mb-2"
          onClick={() => {setShowBooking(rowData)}}
        />
      </div>
    );
  };

  const passengerBodyTemplate = (rowData: any) => {
    const {taximeter} = rowData.stops || {};
    if (taximeter) return `${taximeter.name}`;

    return `${rowData.passenger}`;
  };

  const phoneBodyTemplate = (rowData: any) => {
    const {taximeter} = rowData.stops || {};
    if (taximeter) return `${taximeter.phone}`;

    return `${rowData.phone}`;
  };

  const dateBodyTemplate = (rowData: any) => {
    const date = new Date(rowData.created_at);
    return formatDate(date);
  };

  const canceledBodyTemplate = (rowData: any) => {
    if (!rowData.canceled_at) return;
    
    const date = new Date(rowData.canceled_at);
    return formatDate(date);
  };

  const originAddressBodyTemplate = (rowData: any) => {
    if (rowData.is_callcenter){
      return `${rowData.origin_address?.split(',')[0]}`;
    };

    return `${rowData.origin_address?.split(' - ')[0]}`;
  };

  const destinyAddressBodyTemplate = (rowData: any) => {
    if (rowData.is_callcenter){
      return `${rowData.destiny_address?.split(',')[0]}`;
    };

    return `${rowData.destiny_address?.split(' - ')[0]}`;
  };

  const distanceBodyTemplate = (rowData: any) => {
    return `${(Number(rowData.distance) / 1000).toFixed(1)}`;
  };

  const priceBodyTemplate = (rowData: any) => {
    return `R$ ${(rowData.price/100).toFixed(2)}`;
  };

  const notifiedsBodyTemplate = (rowData: any) => {
    return (
      <div style={{display: 'flex', alignItems: 'center'}}>
        {`${(rowData.notifiedDrivers.length)} `}
        {rowData.notifiedDrivers.length > 0 ? (
          <Button
            icon="pi pi-search"
            className="p-button-rounded p-button-warning p-button-text mr-2 mb-2"
            onClick={() => openNotifieds(rowData)}
        />
        ) : null}
      </div>
    );
  };

  const stateItemTemplate = (state: string) => {
    switch (state) {
      case 'ACCEPTED_DRIVER':
        return (
          <Tag className="mr-2" severity="info" value={state}/>
        );
      case 'CANCELED_DRIVER':
        return (
          <Tag severity="danger" value={state}/>
        );
      case 'CANCELED_PASSENGER':
        return (
          <Tag severity="danger" value={state}/>
        );
      case 'ON_ROUTE':
        return (
          <Tag className="mr-2" severity="info" value={state}/>
        );
      case 'EXPIRED':
        return (
          <Tag severity="danger" value={state}/>
        );
      case 'COMPLETED':
        return (
          <Tag className="mr-2" severity="success" value={state}/>
        );
    
      default:
        return (
          <Tag className="mr-2" value={state}/>
        );
    }
  };

  const stateBodyTemplate = (rowData: any) => {
    if (rowData.stops?.taximeter) return (
      <Tag className="mr-2" value={'TAXÍMETRO'}/>
    );

    return stateItemTemplate(rowData.state);
  };

  const stateFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={states}
        onChange={(e: any) => options.filterCallback(e.value, options.index)}
        itemTemplate={stateItemTemplate}
        placeholder="Select a Status"
        className="p-column-filter"
        showClear
      />
    );
  };

  const driverFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={bookings?.map((booking) => booking.driver).filter((v, i, d) => d.indexOf(v) === i).sort()}
        onChange={(e: any) => options.filterCallback(e.value, options.index)}
        placeholder="Selecione um motorista"
        className="p-column-filter"
        showClear
      />
    );
  };

  const passengerFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={bookings?.map((booking) => booking.passenger).filter((v, i, d) => d.indexOf(v) === i).sort()}
        onChange={(e: any) => options.filterCallback(e.value, options.index)}
        placeholder="Selecione um passageiro"
        className="p-column-filter"
        showClear
      />
    );
  };

  const dateFilterTemplate = (options: any) => {
    return (
      <Calendar
        value={options.value}
        onChange={(e: any) => options.filterCallback(e.value, options.index)}
        dateFormat="mm/dd/yy"
        placeholder="mm/dd/yyyy"
        mask="99/99/9999"
      />
    );
  };

  const cancelBodyTemplate = (rowData: any) => {
    if (
      rowData.state !== 'AWAIT' &&
      rowData.state !== 'ACCEPTED_DRIVER' &&
      rowData.state !== 'EMBARKATION'
    ) return null;
    
    return <Button label="CANCELAR" onClick={() => {
      setCancelBookingId(rowData.id);
      setCancelDialog(true);
    }} className="p-button-danger mr-2 mb-2" />
  }

  const completeBodyTemplate = (rowData: any) => {
    if (
      rowData.state !== 'ON_ROUTE'
    ) return null;
    
    return <Button label="FINALIZAR" onClick={() => {
      setCompleteBookingId(rowData.id);
      setCompleteDialog(true);
    }} className="p-button-info mr-2 mb-2" />
  }

  const notifiedsDialogFooter = (
    <>
      <Button label="Ok" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideNotifiedsDialog} />
    </>
  );

  const detailsDialogFooter = (
    <>
      <Button label="Ok" icon="pi pi-times" className="p-button-text text-red-500" onClick={() => setShowBooking(undefined)} />
    </>
  );

  const cancelDialodFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={() => setCancelDialog(false)} />
      <Button label={canceling ? "Cancelando..." : "Sim, Cancelar!"} disabled={canceling} icon="pi pi-check" className="p-button-text" onClick={() => cancel(cancelBookingId, cancelReason.code)} />
    </>
  );

  const completeDialodFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={() => setCompleteDialog(false)} />
      <Button label={completing ? "Finalizando..." : "Sim, Finalizar!"} disabled={completing} icon="pi pi-check" className="p-button-text" onClick={() => complete(completeBookingId)} />
    </>
  );

  return (
    <div className="grid table-demo">
      {franchise_id && acting_region_id ? null : (
        load ? (
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
        ) : (
          <>
            <div className="card">
              <div className="flex flex-column md:flex-row flex-wrap">
                { !franchise_id ? (
                  <div className="flex flex-column md:flex-row flex-wrap">
                    <label htmlFor="name">Franquia</label>
                    <div className="field col-12 md:col-12">
                      <Dropdown
                        value={franchise}
                        onChange={e => setFranchise(e.value)}
                        options={franchises.map(i => ({ code: i.id, name: i.name }))}
                        optionLabel="name"
                        placeholder="Selecione uma Franquia"
                      />
                    </div>
                  </div>
                ) : null }
  
  
                { !acting_region_id ? (
                  <div className="flex flex-column md:flex-row flex-wrap">
                    <label htmlFor="name">Região</label>
                    <div className="field col-12 md:col-12">
                      {reload ? (
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                      ) : (
                        <Dropdown
                          value={region}
                          onChange={e => setRegion(e.value)}
                          options={regions.map(i => ({ code: i.id, name: i.name }))}
                          optionLabel="name"
                          placeholder="Selecione uma Região"
                        />
                      )}
                    </div>
                  </div>
                ) : null }
              </div>
            </div>
          </>
        )
      )}
      <div className="col-12">
        <div className="card">
          <h5>Corridas</h5>
          <DataTable
            value={bookings}
            paginator
            className="p-datatable-gridlines"
            showGridlines
            rows={10}
            dataKey="id"
            filters={filters}
            filterDisplay="menu"
            loading={loading}
            responsiveLayout="scroll"
            emptyMessage="No customers found.">
            <Column 
              field="id" 
              header="ID" 
              filter 
              filterPlaceholder="Search by id" 
              style={{ minWidth: "12rem" }} 
              body={idBodyTemplate}
            />
            <Column 
              field="driver" 
              header="Motorista" 
              filter 
              filterElement={driverFilterTemplate}
              style={{ minWidth: "12rem" }} 
            />
            {is_callcenter ? null : (
              <Column
                header="Passageiro"
                field="passenger"
                style={{ minWidth: "10rem" }}
                filter
                filterElement={passengerFilterTemplate}
                body={passengerBodyTemplate}
              />
            )}
            {is_callcenter ? null : (
              <Column
                header="Telefone"
                field="phone"
                style={{ minWidth: "10rem" }}
                filter
                filterPlaceholder="Search by name"
                body={phoneBodyTemplate}
              />
            )}
            <Column
              header="Data"
              filterField="created_at"
              dataType="date"
              style={{ minWidth: "10rem" }}
              body={dateBodyTemplate}
              filter
              filterElement={dateFilterTemplate}
            />
            <Column
              header="Cancelado às"
              filterField="canceled_at"
              dataType="date"
              style={{ minWidth: "10rem" }}
              body={canceledBodyTemplate}
              filter
              filterElement={dateFilterTemplate}
            />
            <Column
              header="Origem"
              filterField="origin_address"
              style={{ minWidth: "10rem" }}
              filter
              filterPlaceholder="Search by origin address"
              body={originAddressBodyTemplate}
            />
            <Column
              header="Destino"
              filterField="destiny_address"
              style={{ minWidth: "10rem" }}
              filter
              filterPlaceholder="Search by destiny address"
              body={destinyAddressBodyTemplate}
            />
            <Column
              header="KM"
              filterField="distance"
              style={{ minWidth: "10rem" }}
              body={distanceBodyTemplate}
            />
            <Column
              header="Preço"
              filterField="price"
              style={{ minWidth: "10rem" }}
              body={priceBodyTemplate}
            />
            <Column field="service" header="Serviço" filter filterPlaceholder="Search by service" style={{ minWidth: "12rem" }} />
            <Column
              header="Status"
              filterField="state"
              style={{ minWidth: "10rem" }}
              filter
              body={stateBodyTemplate}
              filterElement={stateFilterTemplate}
            />
            <Column
              header="Notificados"
              style={{ minWidth: "10rem" }}
              body={notifiedsBodyTemplate}
            />
            <Column
              header="Cancelar"
              style={{ minWidth: "10rem" }}
              body={cancelBodyTemplate}
            />
            <Column
              header="Finalizar"
              style={{ minWidth: "10rem" }}
              body={completeBodyTemplate}
            />
          </DataTable>

          {/* Modal motoristas notificados */}
          <Dialog
            visible={notifiedsDialog}
            // style={{ width: "450px" }}
            header="Motoristas notificados"
            modal
            footer={notifiedsDialogFooter}
            onHide={hideNotifiedsDialog}
          >
            <span>{bookingID}</span>
            <hr />

            {notifieds ? notifieds.map(notified => (
              <div className="flex align-items-center justify-content-center">
                <span>
                  {`${notified.name} - ${notified.phone} ${notified.received ? ' >TOCOU<' : ''} - ${notified.version}`}
                </span>
              </div>
            )) : null}
          </Dialog>

          {/* Modal detalhes corrida */}
          <Dialog
            visible={showBooking !== undefined}
            // style={{ width: "450px" }}
            header="Detalhes"
            modal
            footer={detailsDialogFooter}
            onHide={() => setShowBooking(undefined)}
          >
            {showBooking ? (
              <>
                <span>ID: {showBooking.id}</span>
                <hr />

                {showBooking.driver ? (
                  <>
                    <span>Motorista: {showBooking.driver}</span>
                    <hr />
                  </>
                ) : null}

                {showBooking.passenger ? (
                  <>
                    <span>Passageiro: {showBooking.passenger}</span>
                    <hr />
                  </>
                ) : null}

                {showBooking.accepted_at ? (
                  <>
                    <span>Aceita: {formatDate(showBooking.accepted_at)}</span>
                    <hr />
                  </>
                ) : null}

                {showBooking.route_at ? (
                  <>
                    <span>Rota: {formatDate(showBooking.route_at)}</span>
                    <hr />
                  </>
                ) : null}

                {showBooking.completed_at ? (
                  <>
                    <span>Completa: {formatDate(showBooking.completed_at)}</span>
                    <hr />
                  </>
                ) : null}

                {showBooking.canceled_at ? (
                  <>
                    <span>Cancelada: {formatDate(showBooking.canceled_at)}</span>
                    <hr />
                  </>
                ) : null}

                {showBooking.origin_address ? (
                  <>
                    <span>Origem: {showBooking.origin_address}</span>
                    <hr />
                  </>
                ) : null}

                {showBooking.stops?.data ? showBooking.stops.data.map((stop) => (
                  <>
                    <span>Parada: {stop.address}</span>
                    <hr />
                  </>
                )) : null}

                {showBooking.destiny_address ? (
                  <>
                    <span>Destino: {showBooking.destiny_address}</span>
                    <hr />
                  </>
                ) : null}

                {showBooking.distance ? (
                  <>
                    <span>Distância: {`${(Number(showBooking.distance) / 1000).toFixed(1)} km`}</span>
                    <hr />
                  </>
                ) : null}

                {showBooking.distance ? (
                  <>
                    <span>Preço: {`R$ ${(showBooking.price/100).toFixed(2)}`}</span>
                    <hr />
                  </>
                ) : null}
              </>
            ) : null}
          </Dialog>

          {/* Modal cancelar booking */}
          <Dialog
            visible={cancelDialog}
            // style={{ width: "450px" }}
            header="Cancelar a corrida?"
            modal
            footer={cancelDialodFooter}
            onHide={() => setCancelDialog(false)}>
            <div className="flex align-items-center justify-content-center">
              <div className="flex flex-column field col-12 md:col-12">
                <label htmlFor="name">Motivo</label>
                <Dropdown
                  value={cancelReason}
                  onChange={e => setCancelReason(e.value)}
                  options={reasons.map(i => ({ code: i.id, name: i.name }))}
                  optionLabel="name"
                  placeholder="Informe o motivo"
                />
              </div>
            </div>
          </Dialog>

          {/* Modal finalizar booking */}
          <Dialog
            visible={completeDialog}
            // style={{ width: "450px" }}
            header="Finalizar a corrida?"
            modal
            footer={completeDialodFooter}
            onHide={() => setCompleteDialog(false)}>
            <div className="flex align-items-center justify-content-center">
              <label htmlFor="name">Este processo NÃO é recomendado. Forçar a finalização de corridas podem causar bugs no aplicativo do motorista e/ou passageiro.</label>
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Races;
